import { Injectable } from '@angular/core';

import { CameraInversionMode, IAppConfig, KioskConfig, PreferredCamera } from './interfaces';
import { IPrinter } from '@azavista/planner-lib/lib/onsite-checkin/print-server-api';
import { DEFAULT_PRINT_SERVER_PORT } from './const';

export interface IHandheldScannerSettings {
    minimumCodeLength: number;
    maxMsBetweenCharacters: number;
    ignoreSameCodesForMs: number;
    enabled: boolean;
};

export interface IQrCodeScanningSettings {
    cameraInversionMode?: CameraInversionMode;
}

export interface IPrinterSettings {
    dpi: number;
    cutter?: boolean;
}

export function formatPrintServerBaseUrl(port: number): string {
    return `http://localhost:${typeof port === 'number' ? port : DEFAULT_PRINT_SERVER_PORT}`;
}

export const DEFAULT_APP_CONFIG = {
    apiServerBaseUrl: '',
    delayBetweenInitAndStartCamera: 500,
    printServerBaseUrl: formatPrintServerBaseUrl(DEFAULT_PRINT_SERVER_PORT),
    startPrintServerExecutableOnStartUp: true,
    checkPrinterBeforePrinting: true,
    printServerExecutable: './print-server/ZebraPrintServer.exe',
} satisfies IAppConfig;

const STORAGE_KEY_MAP = {
    handheldScanner: 'azavista.settings.handheldScanner',
    qrCodeScanning: 'azavista.settings.qrCodeScanning',
    selectedPrinter: 'azavista.settings.printerSelected',
    printer: 'azavista.settings.printer',
    appConfig: 'azavista.appConfig',
    printingCount: 'azavista.stats.printingCount',
    /** @deprecated now we use `IUser.preferred_event_id` */
    currentEventId: 'azavista.settings.currentEventId',
    selectedBadgeId: (eventId: string) => `azavista.settings.badgeByEventId--${eventId}`,
    kioskConfig: (eventId: string) => `azavista.settings.kioskConfigByEventId--${eventId}`,
};

export const DEFAULT_HANDHELD_SCANNER_SETTINGS: IHandheldScannerSettings = {
    minimumCodeLength: 24,
    maxMsBetweenCharacters: 200,
    ignoreSameCodesForMs: 3000,
    enabled: false
};

export const DEFAULT_QR_CODE_SCANNING_SETTINGS: IQrCodeScanningSettings = {
    cameraInversionMode: CameraInversionMode.doNotUse,
};

export const DEFAULT_PRINTER_SETTINGS: IPrinterSettings = {
    dpi: 300,
    cutter: true,
};

const DEFAULT_KIOS_CONFIG: KioskConfig = {
    categoryFilters: {},
    cameraFacing: PreferredCamera.environment
};

@Injectable({
    providedIn: 'root'
})
export class ConfigurationStorageService {
    private readonly storage = localStorage;

    getPrinterSettings(): IPrinterSettings {
        return this.readItemObject<IPrinterSettings>(STORAGE_KEY_MAP.printer) ?? DEFAULT_PRINTER_SETTINGS;
    }

    setPrinterSettings(settings: IPrinterSettings): void {
        this.setItemObject(STORAGE_KEY_MAP.printer, settings);
    }

    getQrCodeScanningSettings(): IQrCodeScanningSettings {
        return this.readItemObject<IQrCodeScanningSettings>(STORAGE_KEY_MAP.qrCodeScanning) ?? DEFAULT_QR_CODE_SCANNING_SETTINGS;
    }

    setQrCodeScanningSettings(settings: IQrCodeScanningSettings): void {
        this.setItemObject(STORAGE_KEY_MAP.qrCodeScanning, settings);
    }

    getHandheldScannerSettings() {
        const data = this.readItemObject<IHandheldScannerSettings>(STORAGE_KEY_MAP.handheldScanner) ?? DEFAULT_HANDHELD_SCANNER_SETTINGS;
        // TODO: remove the `minimumCodeLength` override once we decided to make it configurable in the UI
        // for now, we always use the default value to override the stored(in localStorage) value
        data.minimumCodeLength = DEFAULT_HANDHELD_SCANNER_SETTINGS.minimumCodeLength;
        return data;
    }

    setHandheldScannerSettings(config: IHandheldScannerSettings) {
        this.setItemObject(STORAGE_KEY_MAP.handheldScanner, config);
    }

    getSelectedPrinter() {
        return this.readItemObject<IPrinter>(STORAGE_KEY_MAP.selectedPrinter);
    }

    getSetlectedBadgeId(eventId: string): string | null {
        return this.readItemString(STORAGE_KEY_MAP.selectedBadgeId(eventId));
    }

    getKioskConfig(eventId: string): KioskConfig {
        return this.readItemObject<KioskConfig>(STORAGE_KEY_MAP.kioskConfig(eventId)) ?? DEFAULT_KIOS_CONFIG;
    }

    getAppConfig(): IAppConfig {
        const config = this.readItemObject<IAppConfig>(STORAGE_KEY_MAP.appConfig) || {};
        return {
            ...DEFAULT_APP_CONFIG,
            ...config,
        };
    }

    /** @deprecated now we use `IUser.preferred_event_id` */
    setCurrentEventId(eventId: string): void {
        this.setItemString(STORAGE_KEY_MAP.currentEventId, eventId);
    }

    /** @deprecated now we use `IUser.preferred_event_id` */
    getCurrentEventId(): string | null {
        return this.readItemString(STORAGE_KEY_MAP.currentEventId);
    }

    getPrintingCount(): number {
        // prevent the `countValue` to have value = undefined
        // because it will fail the Math.max() function
        // Math max is to prevent negative value
        const countValue = this.readItemVariable<number>(STORAGE_KEY_MAP.printingCount) ?? 0;
        return Math.max(countValue, 0);
    }

    setSelectedPrinter(printer: IPrinter) {
        this.setItemObject(STORAGE_KEY_MAP.selectedPrinter, printer);
    }

    setSelectedBadgeId({ eventId, badgeId }: { eventId: string; badgeId: string }) {
        this.setItemString(STORAGE_KEY_MAP.selectedBadgeId(eventId), badgeId);
    }

    setKioskConfig(eventId: string, updatedConfig: Partial<KioskConfig>) {
        const currentConfig = this.getKioskConfig(eventId);
        const updateParam = { ...currentConfig, ...updatedConfig };
        if (updatedConfig?.categoryFilters) {
            updateParam.categoryFilters = {
                ...currentConfig.categoryFilters,
                ...updatedConfig.categoryFilters,
            };
        }
        this.setItemObject(STORAGE_KEY_MAP.kioskConfig(eventId), updateParam);
    }

    setAppConfig(config: Partial<IAppConfig>): IAppConfig {
        const mergedData = {
            ...this.getAppConfig(),
            ...config
        };
        this.setItemObject<Partial<IAppConfig>>(STORAGE_KEY_MAP.appConfig, mergedData);
        return mergedData;
    }

    incrementPrintingCount() {
        this.setItemVariable<number>(STORAGE_KEY_MAP.printingCount, this.getPrintingCount() + 1);
    }

    private readItemVariable<Type>(storageKey: string): Type | null {
        const data = this.readItemString(storageKey);
        return data ? JSON.parse(data) as Type : null;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readItemObject<ObjectType extends { [key: string]: any }>(storageKey: string): ObjectType | null {
        const data = this.readItemString(storageKey);
        return data ? JSON.parse(data) as ObjectType : null;
    }

    private readItemString(storageKey: string): string | null {
        return this.storage.getItem(storageKey);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private setItemObject<ObjectType extends { [key: string]: any }>(storageKey: string, data: ObjectType): void {
        this.setItemString(storageKey, JSON.stringify(data));
    }

    private setItemVariable<Type>(storageKey: string, data: Type): void {
        this.setItemString(storageKey, JSON.stringify(data));
    }

    private setItemString(storageKey: string, data: string): void {
        this.storage.setItem(storageKey, data);
    }

}
